import { useEffect } from 'react';
import { PrefsContext, usePrefsState, useRoute, Route } from './ui/hooks';
import { SearchButton, SettingsButton, TabBar, TopNav } from './ui/components/Shell';
import { CompareHub } from './ui/views/Compare';
import { Train } from './ui/views/Train';
import { Keys } from './ui/views/Keys';
import { PageBoundary } from './ui/components/ErrorBoundary';
import { CORPUS, corpusEntry } from './data/chorales/corpusIndex';
import { PIECES, pathById } from './data';
import { TUNE_NOTES } from './data/chorales/editorial';
import { APP_NAME } from './ui/copy';
import { Mark } from './ui/components/Icons';
import { Home } from './ui/views/Home';
import { Journey } from './ui/views/Journey';
import { PathDetail, PathsIndex } from './ui/views/Paths';
import { Feelings } from './ui/views/Feelings';
import { Walk } from './ui/views/Walk';
import { Method } from './ui/views/Method';
import { Library } from './ui/views/Library';
import { Passion } from './ui/views/Passion';
import { Observatory } from './ui/views/Observatory';
import { TuneRoomView, TunesIndex } from './ui/views/Tunes';
import { FormRoomView, FormsIndex } from './ui/views/Forms';
import { ReturnsRoomView } from './ui/views/Returns';
import { VariationsIndex } from './ui/views/Variations';

/** A title for the browser tab and for shared links, per page. */
function titleOf(route: Route): string {
  const piece = (id: string) => {
    const e = corpusEntry(id);
    if (e) {
      // Sonata titles already carry the K. number: "Piano Sonata in C major, K. 545 · I".
      const [cat, mvt] = e.catalogue.split(' · ');
      return e.title.includes(cat) ? (mvt ? `${e.title} · ${mvt}` : e.title) : `${e.title} (${e.catalogue})`;
    }
    const p = PIECES[id];
    return p ? `${p.title} (${p.catalogue})` : 'A piece';
  };
  switch (route.view) {
    case 'piece':
      return piece(route.id ?? '');
    case 'library':
      return 'Pieces';
    case 'passion':
      return 'One melody, five harmonies';
    case 'tunes':
      return 'One tune, many harmonies';
    case 'tune': {
      const t = CORPUS.tunes.find((x) => x.slug === route.id);
      return t ? `${TUNE_NOTES[t.slug]?.name ?? t.name}: every setting` : 'A tune';
    }
    case 'forms':
      return 'Comparisons';
    case 'form':
      return `${piece(route.id ?? '')}: exposition and recapitulation`;
    case 'returns':
      return route.id ? `${piece(route.id)}: every return` : 'Comparisons';
    case 'variations':
      return 'Theme and variations';
    case 'compare':
      return 'Compare';
    case 'train':
      return 'Train your ear';
    case 'keys':
      return 'The 24 keys';
    case 'observatory':
      return 'The observatory';
    case 'paths':
      return 'Paths';
    case 'path':
      return pathById(route.id ?? '')?.name ?? 'A path';
    case 'feel':
      return 'Listen by feeling';
    case 'walk':
      return 'Walk the terrain';
    case 'method':
      return 'Method';
    default:
      return '';
  }
}

export function App() {
  const prefsState = usePrefsState();
  const route = useRoute();
  useEffect(() => {
    const t = titleOf(route);
    document.title = t ? `${t} · ${APP_NAME}` : `${APP_NAME} · hear how harmony pulls`;
  }, [route]);
  let page;
  switch (route.view) {
    case 'piece':
      page = <Journey pieceId={route.id!} momentId={route.sub} />;
      break;
    case 'library':
      page = <Library />;
      break;
    case 'passion':
      page = <Passion />;
      break;
    case 'observatory':
      page = <Observatory />;
      break;
    case 'tunes':
      page = <TunesIndex />;
      break;
    case 'forms':
      page = <FormsIndex />;
      break;
    case 'form':
      page = <FormRoomView id={route.id ?? ''} />;
      break;
    case 'returns':
      page = route.id ? <ReturnsRoomView id={route.id} /> : <FormsIndex />;
      break;
    case 'variations':
      page = <VariationsIndex />;
      break;
    case 'compare':
      page = <CompareHub />;
      break;
    case 'train':
      page = <Train />;
      break;
    case 'keys':
      page = <Keys />;
      break;
    case 'tune':
      page = <TuneRoomView slug={route.id ?? ''} at={route.sub} />;
      break;
    case 'paths':
      page = <PathsIndex />;
      break;
    case 'path':
      page = <PathDetail id={route.id!} />;
      break;
    case 'feel':
      page = <Feelings focus={route.id} />;
      break;
    case 'walk':
      page = <Walk />;
      break;
    case 'method':
      page = <Method />;
      break;
    default:
      page = <Home />;
  }
  return (
    <PrefsContext.Provider value={prefsState}>
      <div className="shell">
        <a className="skip" href="#main-content" onClick={(e) => { e.preventDefault(); document.getElementById('main-content')?.focus(); }}>
          Skip to content
        </a>
        <header className="topbar">
          <div className="topbar-inner">
            <a className="wordmark" href="#" aria-label={`${APP_NAME}, home`}>
              <Mark />
              <span>{APP_NAME}</span>
            </a>
            <TopNav view={route.view} />
            <span className="topbar-spacer" />
            <SearchButton />
            <SettingsButton />
          </div>
        </header>
        <main className="main" id="main-content" tabIndex={-1}>
          <PageBoundary resetKey={`${route.view}.${route.id ?? ''}.${route.sub ?? ''}`}>{page}</PageBoundary>
        </main>
        <TabBar view={route.view} />
      </div>
    </PrefsContext.Provider>
  );
}
